import React, { useState, useEffect, useCallback } from 'react';
import { Redirect } from 'react-router-dom';
import { Translation, getI18n } from 'react-i18next';
import { Affix, Card, Col, Form, Input, Row, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import { pathTo } from '../../../Routes';
import Logger from '../../../../../lib/Logger';
import AdminPartnerClassInput from '../../adminPartnerClassesAdmin/containers/AdminPartnerClassInputContainer';
import CareGuideColorInput from '../components/CareGuideColorInput';
import { mixpanelEventsEnum } from '../../../../../lib/Mixpanel';

const CareGuidePartnerTagAdminForm = ({
  id,
  data,
  errors,
  load,
  destroyForm,
  isLoading,
  isSubmitting,
  created_id,
  activeId,
  trackerActions,
  ...props
}) => {
  const [color, setColor] = useState('magenta');
  const [redirectTo, setRedirectTo] = useState(null);
  const [form] = Form.useForm();

  // form column settings
  const layout = {
    main: {
      labelCol: { span: 5 },
      wrapperCol: { span: 19 },
    },
  };

  // load record data from API
  useEffect(() => {
    if (id) {
      load(activeId, id);
    }
  }, [activeId, id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      setColor(dataObj.color_hex);
      form.setFieldsValue(dataObj);
    }
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setRedirectTo(
        pathTo('CareGuidePartnerTagAdminEditScreen', {
          id_partnership: activeId,
          id: created_id,
        })
      );
    }
    return () => {
      destroyForm();
    };
  }, [created_id, activeId, setRedirectTo, destroyForm]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `CareGuidePartnerTagAdminForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    for (const input of Object.keys(data)) {
      if (input in values) {
        if (['admin_partner_class'].includes(input)) {
          payload[input + '_id'] = values[input];
        } else {
          payload[input] = values[input];
        }
      }
    }
    payload['partnership_id'] = activeId;

    if (id) {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER
          .CARE_GUIDE_PARTNER_TAGS_ADMIN_EDIT_SUBMIT
      );

      // update
      props.update(id, payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER
          .CARE_GUIDE_PARTNER_TAGS_ADMIN_ADD_SUBMIT
      );

      // create
      props.create(payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `CareGuidePartnerTagForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `CareGuidePartnerTagAdminForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  const removeTracked = useCallback(
    (partnershipId, id, cb) => {
      trackerActions(
        mixpanelEventsEnum.ACTIONS_PARTNER.CARE_GUIDE_PARTNER_TAGS_ADMIN_DELETE
      );
      return props.delete(partnershipId, id, cb);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.delete, trackerActions]
  );

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <Translation>
      {(t) => (
        <>
          <div className='care-guide-partner-tag-form'>
            <Form
              name='care_guide_partner_tag_form'
              form={form}
              initialValues={data}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              validateTrigger='onSubmit'
              {...layout.main}
            >
              <Row gutter={16}>
                <Col xs={24} lg={18}>
                  <Card
                    title={
                      id
                        ? t('care_guide_partner_tag_edit_title')
                        : t('care_guide_partner_tag_add_title')
                    }
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <div className='form-group'>
                      <Form.Item
                        name='label'
                        label={t('care_guide_partner_tag_label')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                          {
                            type: 'string',
                            min: 1,
                            max: 60,
                            message: t('feedback_validation_char_range', {
                              min: 1,
                              max: 60,
                            }),
                          },
                        ]}
                      >
                        <Input disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>

                    <AdminPartnerClassInput
                      form={form}
                      name='admin_partner_class'
                      label={t('care_guide_partner_tag_admin_partner_class')}
                      disabled={isLoading || isSubmitting}
                    />

                    <CareGuideColorInput
                      name={`color_hex`}
                      label={t('care_guide_partner_admin_color_hex')}
                      color={color}
                      setColor={setColor}
                    />
                  </Card>
                </Col>

                <Col xs={24} lg={6}>
                  <Affix offsetTop={10}>
                    <Card title={t('form_metadata_header')}>
                      <FormMetadata
                        id={id}
                        activeDelete={true}
                        partnershipId={activeId}
                        isSubmitting={isSubmitting}
                        delete={removeTracked}
                        deleteRedirectTo='CareGuidePartnerTagsAdminScreen'
                        createdAt={props.createdAt}
                        updatedAt={props.updatedAt}
                      />
                    </Card>
                  </Affix>
                </Col>
              </Row>
            </Form>
          </div>
        </>
      )}
    </Translation>
  );
};

export default CareGuidePartnerTagAdminForm;

Logger.log('silly', `CareGuidePartnerTagAdminForm loaded.`);
